import { FC, Fragment, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useGetActiveBooking } from '../../../bookings/hooks/use-get-active-booking';
import { useGetRebookingInfo } from '../../hooks/use-get-rebooking-info';
import { useGetRoomPrice } from '../../hooks/use-get-room-price';
import { RebookStatus } from '../../api/constants';
import { routes } from '../../../../constants/routes';
import { getTheme } from '../../../../utils/get-theme';

import { RebookingLinksWrap } from './rebooking-links-wrap';
import { RebookingLinkSkeleton } from './rebooking-link-skeleton';
import { RebookingInfo } from './rebooking-info';
import { RebookersKeepItLink } from './rebooking-keep-it';
import { RebookersSwapItLink } from './rebooking-link-swap-it';

export const RebookingLinks: FC = () => {
    const { data: booking } = useGetActiveBooking();
    const { data: rebookingActive, isLoading, isError } = useGetRebookingInfo();
    const [roomCode, setRoomCode] = useState<string>('');
    const { palette, spacing } = useTheme();
    const { t } = useTranslation();
    const { isHosTheme } = getTheme();

    const { isLoading: isLoadingRoomPrice, data: roomPrice } = useGetRoomPrice(roomCode);

    useEffect(() => {
        if (booking && booking.room) {
            setRoomCode(booking.room);
        }
    }, [booking]);

    if (isLoading || isLoadingRoomPrice) {
        return (
            <RebookingLinksWrap>
                <RebookingLinkSkeleton />
                <RebookingLinkSkeleton />
            </RebookingLinksWrap>
        );
    }

    if (
        isError ||
        !rebookingActive ||
        !rebookingActive.isActive ||
        !rebookingActive.opensAt ||
        rebookingActive?.rebookStatus === RebookStatus.NotAllowed ||
        rebookingActive?.rebookStatus === RebookStatus.ComingSoon
    ) {
        return <Navigate to={routes.home} />;
    }

    if (roomPrice && !roomPrice.available) {
        return (
            <RebookingLinksWrap centered={false}>
                <RebookersSwapItLink
                    rebookingOpen={rebookingActive.rebookStatus === RebookStatus.Active}
                    rebookingOpenDate={new Date(rebookingActive.opensAt)}
                    keepItRoomUnavailable
                />
            </RebookingLinksWrap>
        );
    }

    const rebookingDetailsLinkVS = rebookingActive.isPromotionalOffer
        ? 'https://www.vitastudent.com/en/rebookers-discount/'
        : 'https://www.vitastudent.com/en/rebookers';

    const rebookingDetailsLink = isHosTheme
        ? 'https://houseofsocial.com/rebookers/'
        : rebookingDetailsLinkVS;

    return (
        <RebookingLinksWrap>
            <RebookingInfo isPromotionalOffer={rebookingActive.isPromotionalOffer} />

            {roomPrice && roomPrice.available && (
                <Fragment>
                    <Divider
                        textAlign="left"
                        css={{
                            marginTop: spacing(3),
                            ':before': { display: 'none' },
                            '.MuiDivider-wrapper': { paddingLeft: 0 },
                        }}
                    >
                        <Typography
                            fontWeight={700}
                            textTransform="uppercase"
                            color={palette.grey[600]}
                            css={{
                                ':before': {
                                    content: "'01'",
                                    paddingRight: spacing(1),
                                    color: palette.primary.main,
                                },
                            }}
                        >
                            {t('rebooking_info_page-keep_it_divider')}
                        </Typography>
                    </Divider>
                    <RebookersKeepItLink
                        roomCode={roomCode}
                        currency={roomPrice.currency}
                        price={roomPrice.rebooker_base_price || roomPrice.room_price}
                        rebookingOpen={rebookingActive.rebookStatus === RebookStatus.Active}
                        rebookingOpenDate={new Date(rebookingActive.opensAt)}
                        isPromotionalOffer={rebookingActive.isPromotionalOffer}
                        weeksOff={rebookingActive.weeksOff ?? 0}
                        keepItIncentiveValue={rebookingActive.keepItIncentiveValue}
                        isAffordableRoom={booking?.is_affordable_room ?? false}
                    />
                </Fragment>
            )}

            <Box
                display="flex"
                justifyContent="space-between"
                py={4}
                borderBottom={`1px solid ${palette.grey[300]}`}
            >
                <Typography
                    textTransform="uppercase"
                    fontWeight={700}
                    color={palette.grey[600]}
                    display={{ xs: 'none', md: 'block' }}
                >
                    {t('rebooking_info_page-additional_info_divider')}
                </Typography>

                <Link href={rebookingDetailsLink} color={palette.grey[900]}>
                    <Typography fontWeight={700}>
                        {t('rebooking_info_page-additional_info_divider_link')}
                    </Typography>
                </Link>
            </Box>

            <Divider
                textAlign="left"
                css={{
                    marginTop: spacing(3),
                    ':before': { display: 'none' },
                    '.MuiDivider-wrapper': { paddingLeft: 0 },
                }}
            >
                <Typography
                    fontWeight={700}
                    textTransform="uppercase"
                    color={palette.grey[600]}
                    css={{
                        ':before': {
                            content: `'${roomPrice && roomPrice.available ? '02' : '01'}'`,
                            paddingRight: spacing(1),
                            color: palette.primary.main,
                        },
                    }}
                >
                    {t('rebooking_info_page-swap_it_divider')}
                </Typography>
            </Divider>

            <Box mb={4}>
                <RebookersSwapItLink
                    rebookingOpen={rebookingActive.rebookStatus === RebookStatus.Active}
                    rebookingOpenDate={new Date(rebookingActive.opensAt)}
                />
            </Box>
        </RebookingLinksWrap>
    );
};
